import {useCallback, useEffect, useMemo, useState} from 'react';
import {useSelector} from 'react-redux';
import {hasRegistry} from '~/common/utils';
import {RoktJs} from '~/common/utils/RoktJs';
import {selectEmailMd5, selectEmailSha256, selectUserId} from '~/ViewInvitation/selectors/rsvp';
import {selectIsPremium, selectRegistriesData} from '~/ViewInvitation/selectors/eventDetails';
import {selectCharityId, selectCrowdFundPurpose} from '~/ViewInvitation/selectors/eviteEvent';

export interface InitRoktProps {
  emailMd5?: string;
  emailSha256?: string;
}

export const useSuccessPageRokt = () => {
  const userId = useSelector(selectUserId);
  const emailMd5 = useSelector(selectEmailMd5);
  const emailSha256 = useSelector(selectEmailSha256);
  const registriesData = useSelector(selectRegistriesData);
  const charityId = useSelector(selectCharityId);
  const crowdFundPurpose = useSelector(selectCrowdFundPurpose);
  const isPremium = useSelector(selectIsPremium);
  const [initialized, setInitialized] = useState(false);
  const [hasOffers, setHasOffers] = useState(false);
  const eventHasRegistry = hasRegistry(registriesData);

  const pageIdentifier = useMemo(
    () => (isPremium ? 'embedded-PremiumRSVP' : 'embedded-RSVP'),
    [isPremium]
  );

  useEffect(() => {
    if (RoktJs.isReady()) {
      setInitialized(true);
      setHasOffers(RoktJs.getHasOffers());
    }
  }, []);

  const initRokt = useCallback(
    async ({emailMd5: md5, emailSha256: sha256}: InitRoktProps) => {
      if (initialized || RoktJs.isReady()) {
        setInitialized(true);
        setHasOffers(RoktJs.getHasOffers());
        return;
      }
      await RoktJs.initialize(pageIdentifier, {
        emailMd5: md5 || emailMd5,
        emailSha256: sha256 || emailSha256,
        userId,
        charityAdded: charityId !== undefined,
        crowdfundingAdded: crowdFundPurpose !== undefined,
        wishlistAdded: eventHasRegistry,
        isPremium,
      });

      setInitialized(RoktJs.isReady());
      setHasOffers(RoktJs.getHasOffers());
    },
    [
      initialized,
      pageIdentifier,
      emailMd5,
      emailSha256,
      userId,
      charityId,
      crowdFundPurpose,
      eventHasRegistry,
      isPremium,
    ]
  );

  return {
    initRokt,
    hasOffers,
    initialized,
    subscribe: RoktJs.subscribe,
    clearRoktInstance: RoktJs.clearRoktInstance,
  };
};
